import React, { useEffect, useState, useTransition } from "react";
import userService from "../../../app/services/user.service";
import { useSearchParams } from "react-router-dom";
import logo from "../../../assets/img/motion_new_logo.png";
import styles from "./Solution.module.css";
import Loader from "../../../UI/loader/loader";
import { isEmpty } from "../../../utils/custom_funtions";
import useMathjax from "../../../hooks/useMathjax";
import SolutionComponent from "./SolutionComponent";
import footerImage from "../../../assets/img/footer1.jpg";

const Solution = () => {
  const [searchParams] = useSearchParams();
  const [info, setInfo] = useState({});
  const [subjectArr, setSubjectArr] = useState([]);
  const [sectionArr, setSectionArr] = useState([]);
  const [solutionData, setSolutionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isPending, startTransition] = useTransition();

  const getJeeSubjectSortIndex = (subjectName = "") => {
    const name = subjectName.toLowerCase();
    if (name.includes("math")) return 0;
    if (name.includes("phys")) return 1;
    if (name.includes("chem")) return 2;
    return 999;
  };

  const sortSubjectsForJee = (subList, targetId) => {
    const tid = Number(targetId);
    // JEE Main = 1, JEE Advanced = 2
    if (tid !== 1 && tid !== 2) {
      return subList;
    }
    return [...subList].sort(
      (a, b) => getJeeSubjectSortIndex(a) - getJeeSubjectSortIndex(b)
    );
  };

  const getSolition = () => {
    startTransition(() => {
      setLoading(true);
      setError(null);
      userService
        .getPaperInfo(searchParams.get("paperid"))
        .then((res) => {
          // Guard: ensure expected shape exists
          const response = res?.data?.data;
          if (!response || !response.paperdetail) {
            setError("Invalid paper data received.");
            setLoading(false);
            return;
          }

          const datas = { ...response };
          document.title = `Solution_${datas.paperdetail.paper_name || ""}_${datas.paperdetail.to_be_held_on || ""}_${datas.paperdetail.center || ""}`;
          setInfo(datas.paperdetail);

          userService
            .getSolutons(
              searchParams.get("paperid"),
              "'" + (datas.sub_sequence || "") + "'"
            )
            .then((res) => {
              // Guard: ensure data[0] exists and is an object
              const raw = res?.data?.data;
              if (!raw || !raw[0] || typeof raw[0] !== "object") {
                setSolutionData([]);
                setSubjectArr([]);
                setSectionArr([]);
                setLoading(false);
                return;
              }

              const solutionDatas = Object.values(raw[0]);
              // const subList = [
              //   ...new Set(solutionDatas.map((x) => x.subject).filter(Boolean)),
              // ];
              const subList = sortSubjectsForJee(
                [...new Set(solutionDatas.map((x) => x.subject).filter(Boolean))],
                datas.paperdetail.target_id
              );

              const sectionList = [
                ...new Set(solutionDatas.map((x) => x.section_name).filter(Boolean)),
              ];
              setSectionArr(sectionList);
              setSubjectArr(subList);
              setSolutionData(solutionDatas);
              setLoading(false);
            })
            .catch((err) => {
              console.error("getSolutons error:", err);
              setError("Failed to load solutions.");
              setLoading(false);
            });
        })
        .catch((err) => {
          console.error("getPaperInfo error:", err);
          setError("Failed to load paper info.");
          setLoading(false);
        });
    });
  };

  useEffect(() => {
    getSolition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMathjax(solutionData, isPending);

  const setPrintPageSize = (size) => {
    let style = document.getElementById("dynamic-print-page-size");
    if (!style) {
      style = document.createElement("style");
      style.id = "dynamic-print-page-size";
      document.head.appendChild(style);
    }
    style.innerHTML = `
      @media print {
        @page {
          size: ${size};
          margin: .3in .5in .7in .5in;
        }
      }
    `;
  };

  useEffect(() => {
    setPrintPageSize("8.5in 11.7in");
    return () => {
      const style = document.getElementById("dynamic-print-page-size");
      if (style) style.remove();
    };
  }, []);

  // When number_sequence === 3, numbering is continuous per subject across sections
  const isSubjectWiseNumbering = info && info.number_sequence == 3;
  const subjectCounters = {};

  const pageKey = searchParams.get("key");

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  return (
    <div
      style={{ width: "100%", maxWidth: "100%", marginTop: "10px" }}
      className="printable print-page print-solution"
    >
      {/* ── Header ── */}
      <div className={`${styles.printContainer} ${styles.headerContainer}`}>
        <div className={styles.logoContainer}>
          <img src={logo} alt="Logo" />
        </div>
        <p className={styles.title}>
          {pageKey === "0" || pageKey === "1"
            ? "Student's Solution Copy"
            : pageKey === "2"
              ? "Paper Solution"
              : "Teacher's Solution Copy"}
          {` [CODE - ${info.paper_id || ""}]`}
        </p>
        <p className={styles.title}>{info.paper_name || ""}</p>
        <p className={styles.title}>
          {`${info.class || ""} ${info.target || ""} - ${info.phase || ""}`}
        </p>
        <p className={styles.title}>{info.center || ""}</p>
        {info.batch_name && (
          <p className={styles.title}>{info.batch_name}</p>
        )}
        <div className={styles.paperDetail}>
          <p className={styles.title}>Date: {info.to_be_held_on || ""}</p>
          <p className={styles.title}>Duration: {info.duration || ""}</p>
          <p className={styles.title}>
            Max Marks:{" "}
            {isEmpty(info.max_marks) ? info.total_marks : info.max_marks}
          </p>
        </div>
      </div>

      {/* ── Solution Content ── */}
      <div className={`${styles.printContainer} ${styles.solutionBody}`}>
        {subjectArr.map((sub) =>
          sectionArr.map((sec) => {
            const filtered = solutionData.filter(
              (x) => x.subject == sub && x.section_name == sec
            );
            if (isEmpty(filtered)) return null;

            return (
              <div className={styles.section} key={`${sub}-${sec}`}>
                <div className={styles.tableTitle}>
                  <span>
                    {`${pageKey == 4 ? "" : `${sub} -`} ${sec}`}
                  </span>
                </div>
                <div className={styles.sectionSeparator}></div>

                {pageKey == 0 ||
                  pageKey == 3 ||
                  pageKey == 4 ||
                  pageKey == 5 ? (
                  // Single-column layout
                  filtered.map((data) => {
                    const nextNum = (subjectCounters[sub] =
                      (subjectCounters[sub] || 0) + 1);
                    const overrideData = isSubjectWiseNumbering
                      ? { ...data, display_number: nextNum }
                      : data;
                    return (
                      <SolutionComponent
                        key={data.qid || nextNum}
                        solutionData={overrideData}
                        pageKey={pageKey}
                      />
                    );
                  })
                ) : (
                  // Two-column layout (keys 1, 2)
                  // CSS columns: numbers flow top-to-bottom in left column first:
                  //   1 | 4
                  //   2 | 5
                  //   3 | 6
                  <div className={styles.twoColumnFlow}>
                    {filtered.map((data) => {
                      const nextNum = (subjectCounters[sub] =
                        (subjectCounters[sub] || 0) + 1);
                      const overrideData = isSubjectWiseNumbering
                        ? { ...data, display_number: nextNum }
                        : data;
                      return (
                        <div
                          key={data.qid || nextNum}
                          className={styles.solutionItem}
                        >
                          <SolutionComponent
                            solutionData={overrideData}
                            pageKey={pageKey}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Footer ── */}
      <div className={styles.footer}>
        <img
          src={footerImage}
          alt="Footer"
          style={{ width: "100%", height: "35px" }}
        />
      </div>
    </div>
  );
};

export default Solution;
